/**
 * Product Catalog Type Definitions
 * Based on docs/3C_DATA_MODELS.md section 3
 * 
 * NAMING CONVENTION: `title` vs `name`
 * ====================================
 * - SOURCE types (EditorPick): Use `title` as primary (from catalogs/APIs)
 * - CONSUMER types (CartItem, WishlistItem): Use `name` as primary (for UI display)
 * - Data-loading code maps `title` → `name` at boundaries (catalogService, useEditorPicks)
 * - Always use fallback pattern: `item.name || item.title || 'Product'`
 * 
 * See docs/3C_DATA_MODELS.md for detailed field usage guidelines.
 */

import { ProductSource } from "./profile";

/** Promotion badge types for sponsored/featured products */
export type PromotionBadge = 'sponsored' | 'featured' | 'seasonal' | 'new' | 'editors-pick';

/** Affiliate source types for revenue tracking */
export type AffiliateSource = 'amazon' | 'coupang' | 'iherb' | 'yesstyle' | 'direct' | 'inhouse' | 'partner';

export interface EditorPick {
  id: string;
  title: string;
  name?: string;
  score: number; // 0-100 percentage
  reason: string;
  priceUSD: number;
  image: string;
  url: string;
  brand?: string;
  matchedConcerns?: string[];
  /** Promotion badge type (if promoted) */
  promotionBadge?: PromotionBadge;
  /** Promotion name for tooltip */
  promotionName?: string;
  
  // Affiliate tracking fields
  /** Affiliate URL for monetization (Amazon, Coupang, etc.) */
  affiliateUrl?: string;
  /** Source of affiliate link */
  affiliateSource?: AffiliateSource;
  /** Internal tracking ID for click attribution */
  trackingId?: string;
  /** Cost-per-click bid for sponsored products */
  cpcBid?: number;
  /** Revenue share percentage (e.g., 0.07 = 7%) */
  revenueShare?: number;
  /** Whether this is a paid/sponsored placement */
  isPaidPlacement?: boolean;
}

/**
 * Cart item for shopping cart display and checkout.
 * Uses `name` (consumer type) - should be populated from `title` at data boundary.
 */
export interface CartItem {
  id: string;
  /** Product name for display (normalized from title) */
  name: string;
  priceUSD: number;
  price?: number; // Legacy fallback - prefer priceUSD
  image: string;
  url: string;
  brand?: string;
  quantity: number;
  source: ProductSource;
}

/**
 * Wishlist Item - products saved for later
 */
export interface WishlistItem {
  id: string;
  name: string;
  priceUSD: number;
  image: string;
  url: string;
  brand?: string;
  addedAt?: number; // Timestamp when added to wishlist
  /** Product source - determines if external link is shown */
  source?: 'inhouse' | 'partner' | 'whitelist' | 'web' | 'search';
}

/**
 * Determines if a product URL is internal (in-store) or external (web).
 * In-store products use internal URLs that don't start with http/https.
 * 
 * Currently supported in-store sources:
 * - In-house: /catalogs/products/SKU or /catalogs/...
 * - Future partners can be added with similar internal URL patterns
 * 
 * @param url - Product URL to check
 * @returns true if the product is an in-store product (should show in panel, not external link)
 */
export function isInStoreProduct(url: string | undefined): boolean {
  if (!url) return false;
  
  // External URLs start with http:// or https://
  // Internal/in-store URLs are relative paths like /catalogs/products/SKU
  const isExternalUrl = url.startsWith('http://') || url.startsWith('https://');
  
  return !isExternalUrl;
}

// =============================================================================
// Sponsored Slots Types (6-Tile Affiliate Grid)
// =============================================================================

/**
 * Sponsored Slot - paid/affiliate product placement
 * Stored in Firestore sponsoredSlots collection
 * Displayed as 6 tiles (2 rows × 3 cols) when INHOUSE_SELLING_ENABLED is off
 */
export interface SponsoredSlot {
  /** Firestore document ID */
  id: string;
  /** Slot position (0-5) */
  slotIndex: number;
  /** Product details */
  product: {
    id: string;
    name: string;
    brand: string;
    image: string;
    priceUSD: number;
    description?: string;
  };
  /** Affiliate tracking */
  affiliateUrl: string;
  affiliateSource: AffiliateSource;
  /** Cost-per-click bid in USD */
  cpcBid: number;
  /** Revenue share percentage (e.g., 0.07 = 7%) */
  revenueShare?: number;
  /** Scheduling */
  activeFrom: Date | string;
  activeTo: Date | string;
  /** Targeting (optional) */
  targeting?: {
    skinTypes?: string[];
    concerns?: string[];
    regions?: string[];
  };
  /** Status */
  isActive: boolean;
  /** Analytics */
  impressions?: number;
  clicks?: number;
  revenue?: number;
  /** Timestamps */
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * Product source for grid display
 * Indicates which row/source the product came from
 */
export type ProductGridSource = 'ai' | 'web-search' | 'sponsored';

/**
 * Unified product card for 9-tile grid
 * Used to normalize products from different sources
 */
export interface GridProductCard {
  /** Unique identifier */
  id: string;
  /** Product name */
  name: string;
  /** Brand name */
  brand?: string;
  /** Product image URL */
  image: string;
  /** Price in USD */
  priceUSD: number;
  /** Product or affiliate URL */
  url: string;
  /** Source of this product (for row assignment) */
  source: ProductGridSource;
  /** Match score (0-100) for AI picks */
  score?: number;
  /** Reason for recommendation */
  reason?: string;
  /** Affiliate source if applicable */
  affiliateSource?: AffiliateSource;
  /** Whether this is a sponsored/paid placement */
  isSponsored?: boolean;
  /** Tracking ID for click attribution */
  trackingId?: string;
}
